import type {
  ActionType,
  SalesAction,
  SalesActionCreateRequest,
  SalesStatus,
} from '@shared/types/sales-action';
import { isNextActionAllowedForStatus } from '@shared/constants/next-action-matrix';

/// 商談化 (商談前 → 商談以降) で元行から引き継ぐフィールド群。
/// note / 成果 / 契約 / 履歴は引き継がない (商談行は新しい案件単位)。
type DealTransitionRow = Pick<
  SalesAction,
  | 'target'
  | 'candidate_product_ids'
  | 'participant_lawyer_ids'
  | 'action_type'
  | 'action_title'
  | 'due_date'
  | 'source_customer_problem_id'
>;

export interface DealTransitionPlan {
  /// 元行の昇格: 単一 product_id + 商談以降ステータス。
  promote: {
    solutionId: string;
    salesStatus: SalesStatus;
    /// 昇格後に元行が持つ次アクション。商談化先で不許可なら null。
    actionType: ActionType | null;
  };
  /// 元行に残っている候補をクリアするか (商談行に候補を残さない)。
  clearCandidates: boolean;
  /// 行分割で新規作成する商談行 (各行はちょうど 1 ソリューション)。
  creates: SalesActionCreateRequest[];
}

/// 商談前行を商談以降へ進める計画を作る。 `solutionIds` の先頭を元行へ昇格し、
/// 残りを「1ソリューション1行」 の新規商談行として作成する (行分割)。
///
/// 不変条件: 生成される商談行 (昇格 1 + 新規 creates.length) はそれぞれ単一の
/// product_id を持ち、 候補 (candidate_product_ids) は引き継がない。
/// 分割は元行の派生なので、 新規行のアンカーは**元行の保存アンカー** (`action.target`)
/// を使う。 画面ターゲットではない — 弁護士詳細から firm アンカー行を分割しても
/// 新規行は firm アンカーのまま (86eya0dva PR3。 backend の clone と同じ意味論)。
export function planDealTransition(
  action: DealTransitionRow,
  targetStatus: SalesStatus,
  solutionIds: string[],
): DealTransitionPlan {
  const first = solutionIds[0];
  if (first === undefined) {
    throw new Error('planDealTransition requires at least one solution');
  }
  const rest = solutionIds.slice(1);
  // 商談化先のステータスで許可されない action_type は引き継がない (旧ステータス用の
  // 次アクションが商談行へ漏れるのを防ぐ。例: appointment/pre_hearing → negotiation_c)。
  const actionType = isNextActionAllowedForStatus(targetStatus, action.action_type)
    ? action.action_type
    : null;
  return {
    promote: { solutionId: first, salesStatus: targetStatus, actionType },
    clearCandidates: action.candidate_product_ids.length > 0,
    creates: rest.map(
      (solutionId): SalesActionCreateRequest => ({
        target: action.target,
        participant_lawyer_ids: action.participant_lawyer_ids,
        product_id: solutionId,
        sales_status: targetStatus,
        action_type: actionType,
        action_title: action.action_title,
        due_date: action.due_date,
        source_customer_problem_id: action.source_customer_problem_id,
      }),
    ),
  };
}
